/**
 * RoleGate - wraps the entire Risk Mgmt Panel.
 *
 * Default allowed roles: `RiskManager` OR `SuperAdmin`. Anything else
 * gets the "no access" landing.
 *
 * Nested routes (e.g. Risk Approvals queue/history/approvers) can pass
 * `allowedRoles` to widen the gate — for example, to allow
 * `RiskApprover` users who aren't full Risk Managers to act on
 * approval requests.
 *
 * Per locked decision 2026-04-27 (feedback_risk_mgmt_not_in_teams_app +
 * project_risk_mgmt_portal_v01_scope), Risk Mgmt staff are NOT Teams app
 * users. This panel is their standalone surface, mirroring sysops-panel
 * pattern at portal.mypcb.com/risk-mgmt/.
 */

import { type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AccessDenied } from '@/modules/AccessDenied/AccessDenied';

const DEFAULT_ALLOWED_ROLES = ['RiskManager', 'SuperAdmin'] as const;

export interface RoleGateProps {
  children: ReactNode;
  /**
   * Optional override for nested route gates. When provided, the user
   * needs at least one of these roles to pass (instead of the default
   * panel-wide list).
   */
  allowedRoles?: ReadonlyArray<string>;
}

export function RoleGate({ children, allowedRoles }: RoleGateProps): JSX.Element {
  const { user, status, loading, error, refresh } = useAuth();
  const roles = allowedRoles ?? DEFAULT_ALLOWED_ROLES;

  if (loading) {
    return <div className="loading-screen">Signing in to Risk Mgmt Panel...</div>;
  }

  if (status === 'error') {
    return (
      <div className="auth-gate">
        <div className="auth-gate__card">
          <h1 className="auth-gate__title">Sign-in failed</h1>
          <p className="auth-gate__body">
            {error ?? 'Unable to complete Entra sign-in.'}
          </p>
          <button className="btn btn--primary" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <div className="loading-screen">Resolving identity…</div>;
  }

  const hasAllowedRole = user.roles.some((r) =>
    (roles as readonly string[]).includes(r),
  );

  if (!hasAllowedRole) {
    return <AccessDenied user={user} />;
  }

  return <>{children}</>;
}
